// `agregar_al_moodboard` y `editar_moodboard`: el agente ARMA y CURA los moodboards de
// la marca — ADR-2026-09-13-el-agente-arma-moodboards.
//
// Hasta acá los moodboards eran de sólo lectura para el agente (`ver_moodboard`,
// ADR-2026-09-13-el-agente-ve-los-moodboards): podía inspirarse en ellos pero no
// tocarlos, y ni siquiera el usuario podía mandar una imagen generada a una carpeta sin
// bajarla y volverla a subir a mano. Ahora el agente puede crear una carpeta, renombrarla,
// pintarla, sacarle fotos y —lo que de verdad importa— meterle imágenes de CUALQUIER
// lado de la app: una generada del chat, la foto de un producto o de un modelo, el logo,
// lo que el usuario adjuntó, o una foto de otro moodboard.
//
// Dos herramientas y no cinco: "crear" está adentro de agregar (`crear_si_no_existe`),
// porque "guardá estas tres en un moodboard nuevo que se llame Verano" es UN pedido y no
// tiene por qué costar dos llamadas. Y no hay ninguna para BORRAR una carpeta entera: es
// lo único irreversible del conjunto y una colección curada a mano no se tira sin
// intención (se borra desde Mi marca, con su confirmación).
//
// Acá está sólo lo puro: el contrato, la validación de lo que mandó el modelo y el texto
// del resultado. La red —listar, crear, subir, borrar— se inyecta desde `ChatAtlasContext`,
// que es quien sabe de qué empresa y marca es el chat. Mismo molde que `verMoodboard.ts`.

import { COLORES_DE_CARPETA } from '../pages/v2/moodboardsApi';
import type { HerramientaChat } from './tipos';
import { elegirMoodboard, type MoodboardResumen } from './verMoodboard';

export const NOMBRE_AGREGAR_AL_MOODBOARD = 'agregar_al_moodboard';
export const NOMBRE_EDITAR_MOODBOARD = 'editar_moodboard';

/** De dónde puede salir una foto que va al moodboard. Los nombres son los MISMOS que usa
 *  `reference_images` en las generaciones: el modelo ya habla ese vocabulario, y una
 *  segunda taxonomía para lo mismo sólo se contradice con la primera. */
export const FUENTES_DE_FOTO = [
  'generada',
  'producto',
  'modelo',
  'logo',
  'adjunto',
  'moodboard',
] as const;

export type FuenteDeFoto = (typeof FUENTES_DE_FOTO)[number];

export interface FotoPedida {
  source: FuenteDeFoto;
  /** `source: 'generada'`. */
  image_id?: string;
  /** `source: 'producto'`. */
  product_id?: string;
  /** `source: 'modelo'`. */
  model_id?: string;
  /** `source: 'moodboard'`: de qué carpeta sale. */
  moodboard_origen?: string;
  /** Posición (base 1) dentro de las adjuntas del usuario o del moodboard de origen. */
  numero?: number;
  /** `source: 'adjunto'`: una adjunta puntual por su id — de un mensaje anterior ("m1-2") o
   *  traída de una página ("web-…", ADR-2026-10-15-el-agente-abre-paginas-web). */
  adjunto_id?: string;
}

/** Tope por llamada. Cada foto es una bajada + una subida: veinte ya es una espera larga,
 *  y el agente puede volver a llamar. */
export const MAX_FOTOS_POR_LLAMADA = 20;

const IDS_DE_COLOR = COLORES_DE_CARPETA.map((c) => c.id);

export const HERRAMIENTA_AGREGAR_AL_MOODBOARD: HerramientaChat = {
  name: NOMBRE_AGREGAR_AL_MOODBOARD,
  description:
    'Guarda imágenes en un ESTILO de la marca (carpeta de referencias de estilo; en la app, Mi marca → Estilos: con el usuario decí "estilo", nunca "moodboard"). Las imágenes salen de donde ya estén: una que generaste en el chat, la foto de un producto o de un modelo del catálogo, el logo, algo que el usuario adjuntó, o una foto de otro moodboard. Si el moodboard no existe, con `crear_si_no_existe` lo creás en la misma llamada.\n' +
    'Usala cuando el usuario quiere GUARDAR inspiración: "sumá esta al estilo Verano", "armame un estilo con estas cinco", "guardá esa referencia para la campaña". Es silenciosa y gratis, pero ESCRIBE en la marca: hacela cuando te lo pidan o cuando ya lo hayas propuesto y te hayan dicho que sí, nunca de motu propio para "dejar ordenado".\n' +
    'Un estilo es inspiración, no un álbum: no le metas todas las fotos de una tanda ni fotos de producto sobre fondo blanco. Después de guardar, contale en una línea qué guardaste y dónde. Método: skill `moodboard`.',
  input_schema: {
    type: 'object',
    properties: {
      moodboard: {
        type: 'string',
        description:
          'Nombre de la carpeta (como figura en el catálogo) o su id. Si el usuario adjuntó una carpeta, el contexto del mensaje te da el id: pasá ESE, que es exacto.',
      },
      crear_si_no_existe: {
        type: 'boolean',
        description:
          'true = si no hay ninguna con ese nombre, la crea. Default false: sin esto, un nombre que no existe es un error y te devuelve los que sí hay (así un typo no te crea una carpeta de más).',
      },
      color: {
        type: 'string',
        description: `OPCIONAL, sólo al crear. Uno de: ${IDS_DE_COLOR.join(', ')}. Sin esto queda el default.`,
      },
      imagenes: {
        type: 'array',
        description: `Qué guardar, en orden. Hasta ${MAX_FOTOS_POR_LLAMADA} por llamada.`,
        items: {
          type: 'object',
          properties: {
            source: {
              type: 'string',
              enum: [...FUENTES_DE_FOTO],
              description:
                '"generada" (una imagen del chat, `image_id`), "producto" (`product_id`), "modelo" (`model_id`), "logo" (el logo de la marca), "adjunto" (una imagen que adjuntó el usuario en su mensaje), "moodboard" (una foto de otra carpeta: `moodboard_origen` + `numero`).',
            },
            image_id: { type: 'string', description: 'Si source="generada".' },
            adjunto_id: {
              type: 'string',
              description: 'Con source="adjunto": una imagen puntual por su id ("web-…" de abrir_web, o "m1-2" de un mensaje anterior).',
            },
            product_id: { type: 'string', description: 'Si source="producto".' },
            model_id: { type: 'string', description: 'Si source="modelo".' },
            moodboard_origen: {
              type: 'string',
              description: 'Si source="moodboard": nombre o id de la carpeta de la que sale la foto.',
            },
            numero: {
              type: 'number',
              description:
                'La POSICIÓN, contando desde 1. Con source="moodboard", la que numeró ver_moodboard (#3 → 3). Con source="adjunto", cuál de las que adjuntó; omitilo si adjuntó una sola o si las querés todas.',
            },
          },
          required: ['source'],
        },
      },
    },
    required: ['moodboard', 'imagenes'],
  },
};

export const HERRAMIENTA_EDITAR_MOODBOARD: HerramientaChat = {
  name: NOMBRE_EDITAR_MOODBOARD,
  description:
    'Cambia un MOODBOARD que ya existe: renombrarlo, pintarle el ícono de otro color, o SACARLE fotos. Podés hacer varias cosas en la misma llamada.\n' +
    'Sacar una foto la BORRA de la carpeta y no se puede deshacer: pedile confirmación al usuario antes, salvo que te lo esté pidiendo él con esas palabras. Para agregar fotos es `agregar_al_moodboard`; para ver qué hay adentro y en qué orden, `ver_moodboard` (las posiciones de acá son las que numera esa).\n' +
    'No existe borrar la carpeta entera: eso se hace desde Mi marca → Estilos. Si te lo piden, decilo así.',
  input_schema: {
    type: 'object',
    properties: {
      moodboard: {
        type: 'string',
        description: 'Nombre de la carpeta (como figura en el catálogo) o su id.',
      },
      nuevo_nombre: { type: 'string', description: 'OPCIONAL. Cómo pasa a llamarse.' },
      color: {
        type: 'string',
        description: `OPCIONAL. Uno de: ${IDS_DE_COLOR.join(', ')}.`,
      },
      sacar_fotos: {
        type: 'array',
        description:
          'OPCIONAL. Las POSICIONES de las fotos a borrar, contando desde 1 y tal como las numeró ver_moodboard (#2 y #5 → [2, 5]). Miralas antes: sin verlas no sabés cuál estás borrando.',
        items: { type: 'number' },
      },
    },
    required: ['moodboard'],
  },
};

// ─── Lectura de lo que mandó el modelo ────────────────────────────────

const texto = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null;

export function campoDeTexto(input: unknown, campo: string): string | null {
  return texto((input as Record<string, unknown> | null)?.[campo]);
}

/** El color pedido, sólo si es uno de los del catálogo. Uno inventado se ignora en vez de
 *  guardarse: la base acepta cualquier string y el ícono caería al default sin avisar. */
export function colorPedido(input: unknown): string | null {
  const c = campoDeTexto(input, 'color');
  return c && IDS_DE_COLOR.includes(c) ? c : null;
}

export function creaSiNoExiste(input: unknown): boolean {
  return (input as { crear_si_no_existe?: unknown } | null)?.crear_si_no_existe === true;
}

/** Las fotos pedidas, limpias: sin las que no dicen de dónde salen y con el tope aplicado. */
export function fotosPedidas(input: unknown): FotoPedida[] {
  const bruto = (input as { imagenes?: unknown } | null)?.imagenes;
  if (!Array.isArray(bruto)) return [];
  const out: FotoPedida[] = [];
  for (const item of bruto) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const source = o.source;
    if (typeof source !== 'string' || !FUENTES_DE_FOTO.includes(source as FuenteDeFoto)) continue;
    const foto: FotoPedida = { source: source as FuenteDeFoto };
    for (const k of ['image_id', 'product_id', 'model_id', 'moodboard_origen', 'adjunto_id'] as const) {
      const v = texto(o[k]);
      if (v) foto[k] = v;
    }
    if (typeof o.numero === 'number' && Number.isFinite(o.numero) && o.numero >= 1) {
      foto.numero = Math.floor(o.numero);
    }
    out.push(foto);
    if (out.length >= MAX_FOTOS_POR_LLAMADA) break;
  }
  return out;
}

/** Las posiciones a sacar: enteras, desde 1, sin repetir y de mayor a menor (borrar de
 *  atrás para adelante deja intactas las posiciones que todavía no se tocaron). */
export function posicionesASacar(input: unknown): number[] {
  const bruto = (input as { sacar_fotos?: unknown } | null)?.sacar_fotos;
  if (!Array.isArray(bruto)) return [];
  const vistas = new Set<number>();
  for (const v of bruto) {
    if (typeof v === 'number' && Number.isFinite(v) && v >= 1) vistas.add(Math.floor(v));
  }
  return [...vistas].sort((a, b) => b - a);
}

/** Cómo se le describe a una foto pedida cuando algo sale mal. */
export function describirFoto(f: FotoPedida): string {
  switch (f.source) {
    case 'generada':
      return `la generada ${f.image_id ?? '(sin image_id)'}`;
    case 'producto':
      return `la foto del producto ${f.product_id ?? '(sin product_id)'}`;
    case 'modelo':
      return `la foto del modelo ${f.model_id ?? '(sin model_id)'}`;
    case 'logo':
      return 'el logo';
    case 'adjunto':
      return f.adjunto_id ? `la adjunta ${f.adjunto_id}` : f.numero ? `la adjunta #${f.numero}` : 'la adjunta';
    case 'moodboard':
      return `la foto ${f.numero ? `#${f.numero} ` : ''}de "${f.moodboard_origen ?? '?'}"`;
  }
}

// ─── Resolución (la red se inyecta) ───────────────────────────────────

/** Una foto ya localizada, lista para copiarse a la carpeta. */
export interface FotoResuelta {
  pedida: FotoPedida;
  url: string;
}

export interface FotoNoResuelta {
  pedida: FotoPedida;
  motivo: string;
}

export interface Resueltas {
  ok: FotoResuelta[];
  fallidas: FotoNoResuelta[];
}

const sinCarpeta = (pedido: string, carpetas: MoodboardResumen[]): string =>
  carpetas.length === 0
    ? `No hay ningún estilo que se llame "${pedido}": la marca no tiene estilos todavía.`
    : `No hay ningún estilo que se llame "${pedido}". Los que hay: ${carpetas
        .map((c) => `"${c.nombre}"`)
        .join(', ')}.`;

export interface DepsAgregar {
  listar: () => Promise<MoodboardResumen[]>;
  crear: (nombre: string, color: string | null) => Promise<MoodboardResumen>;
  /** Localiza cada foto pedida. Recibe el catálogo FRESCO que ya trajo `listar`: la
   *  fuente `moodboard` necesita resolver la carpeta de origen y el cache del prompt
   *  puede estar vacío o viejo. */
  resolver: (pedidas: FotoPedida[], carpetas: MoodboardResumen[]) => Promise<Resueltas>;
  /** Copia una foto ya localizada a la carpeta. */
  guardar: (moodboardId: string, url: string) => Promise<void>;
  /** Para que el catálogo del prompt y la biblioteca queden al día. */
  refrescar: () => void;
}

export async function resolverAgregarAlMoodboard(
  input: unknown,
  deps: DepsAgregar,
): Promise<{ __texto: string; __error?: true }> {
  const pedido = campoDeTexto(input, 'moodboard');
  if (!pedido) {
    return { __texto: 'Falta `moodboard`: el nombre de la carpeta donde guardar.', __error: true };
  }
  const pedidas = fotosPedidas(input);
  if (pedidas.length === 0) {
    return {
      __texto: 'No pasaste ninguna imagen válida en `imagenes`. Cada una necesita su `source`.',
      __error: true,
    };
  }

  const carpetas = await deps.listar();
  let carpeta = elegirMoodboard(pedido, carpetas);
  let recienCreada = false;
  if (!carpeta) {
    if (!creaSiNoExiste(input)) {
      return {
        __texto: `${sinCarpeta(pedido, carpetas)} Si querés una nueva, volvé a llamar con crear_si_no_existe: true.`,
        __error: true,
      };
    }
    carpeta = await deps.crear(pedido, colorPedido(input));
    recienCreada = true;
  }

  const { ok, fallidas } = await deps.resolver(pedidas, carpetas);
  const guardadas: FotoResuelta[] = [];
  const noGuardadas = [...fallidas];
  for (const foto of ok) {
    try {
      await deps.guardar(carpeta.id, foto.url);
      guardadas.push(foto);
    } catch (e) {
      noGuardadas.push({ pedida: foto.pedida, motivo: String(e instanceof Error ? e.message : e) });
    }
  }
  deps.refrescar();

  const partes: string[] = [];
  if (recienCreada) partes.push(`Creé el estilo "${carpeta.nombre}".`);
  if (guardadas.length > 0) {
    partes.push(
      `Guardé ${guardadas.length} foto(s) en "${carpeta.nombre}": ${guardadas
        .map((f) => describirFoto(f.pedida))
        .join(', ')}.`,
    );
  }
  if (noGuardadas.length > 0) {
    partes.push(
      `No pude guardar ${noGuardadas.length}: ${noGuardadas
        .map((f) => `${describirFoto(f.pedida)} (${f.motivo})`)
        .join('; ')}.`,
    );
  }
  if (guardadas.length === 0) {
    partes.push('No entró ninguna. Revisá los ids o preguntale al usuario cuáles quería.');
    return { __texto: partes.join(' '), __error: true };
  }
  partes.push('Contale al usuario qué guardaste y dónde, en una línea.');
  return { __texto: partes.join(' ') };
}

export interface DepsEditar {
  listar: () => Promise<MoodboardResumen[]>;
  renombrar: (moodboardId: string, nombre: string) => Promise<void>;
  pintar: (moodboardId: string, color: string) => Promise<void>;
  /** Las fotos en el MISMO orden en que las numera `ver_moodboard`. */
  fotosDe: (moodboardId: string) => Promise<Array<{ id: string; path: string }>>;
  sacar: (foto: { id: string; path: string }) => Promise<void>;
  refrescar: () => void;
}

export async function resolverEditarMoodboard(
  input: unknown,
  deps: DepsEditar,
): Promise<{ __texto: string; __error?: true }> {
  const pedido = campoDeTexto(input, 'moodboard');
  if (!pedido) return { __texto: 'Falta `moodboard`: cuál querés editar.', __error: true };

  const carpetas = await deps.listar();
  const carpeta = elegirMoodboard(pedido, carpetas);
  if (!carpeta) return { __texto: sinCarpeta(pedido, carpetas), __error: true };

  const nuevoNombre = campoDeTexto(input, 'nuevo_nombre');
  const color = colorPedido(input);
  const posiciones = posicionesASacar(input);
  if (!nuevoNombre && !color && posiciones.length === 0) {
    return {
      __texto: `No pediste ningún cambio para "${carpeta.nombre}". Pasá \`nuevo_nombre\`, \`color\` o \`sacar_fotos\`.`,
      __error: true,
    };
  }

  const hechos: string[] = [];
  const problemas: string[] = [];

  if (posiciones.length > 0) {
    const fotos = await deps.fotosDe(carpeta.id);
    const sacadas: number[] = [];
    for (const pos of posiciones) {
      const foto = fotos[pos - 1];
      if (!foto) {
        problemas.push(`la #${pos} no existe (la carpeta tiene ${fotos.length})`);
        continue;
      }
      try {
        await deps.sacar(foto);
        sacadas.push(pos);
      } catch (e) {
        problemas.push(`la #${pos} no se pudo borrar (${String(e instanceof Error ? e.message : e)})`);
      }
    }
    if (sacadas.length > 0) {
      hechos.push(
        `saqué ${sacadas.length} foto(s) (${sacadas.sort((a, b) => a - b).map((p) => `#${p}`).join(', ')})`,
      );
    }
  }

  if (nuevoNombre && nuevoNombre !== carpeta.nombre) {
    try {
      await deps.renombrar(carpeta.id, nuevoNombre);
      hechos.push(`ahora se llama "${nuevoNombre}"`);
    } catch (e) {
      problemas.push(`no se pudo renombrar (${String(e instanceof Error ? e.message : e)})`);
    }
  }

  if (color) {
    try {
      await deps.pintar(carpeta.id, color);
      hechos.push(`quedó ${color}`);
    } catch (e) {
      problemas.push(`no se pudo cambiar el color (${String(e instanceof Error ? e.message : e)})`);
    }
  }

  deps.refrescar();

  if (hechos.length === 0) {
    return {
      __texto: `No cambié nada en "${carpeta.nombre}": ${problemas.join('; ') || 'nada para hacer'}.`,
      __error: true,
    };
  }
  const cola = problemas.length > 0 ? ` No pude: ${problemas.join('; ')}.` : '';
  return { __texto: `Estilo "${carpeta.nombre}": ${hechos.join(', ')}.${cola}` };
}
